// The live key bindings, read once at the shell and handed to everything that
// listens for a key. Overrides live in the user's server-side preferences
// (hooks/usePreferences.ts) under a single `shortcuts` key, so a rebind
// follows the person to another browser.
import { createContext, useContext, useMemo, useCallback } from "react";
import { usePreferences } from "@/hooks/usePreferences";
import { DEFAULT_BINDINGS, PREF_KEY, mergeBindings, type Bindings, type ShortcutId } from "@/lib/shortcuts";

type Ctx = {
  bindings: Bindings;
  setBinding: (id: ShortcutId, combos: string[]) => void;
  resetAll: () => void;
};

const ShortcutsContext = createContext<Ctx>({
  bindings: DEFAULT_BINDINGS,
  setBinding: () => {},
  resetAll: () => {},
});

export function ShortcutsProvider({ children }: { children: React.ReactNode }) {
  const { prefs, setPref } = usePreferences();
  const bindings = useMemo(() => mergeBindings(prefs[PREF_KEY]), [prefs]);

  const setBinding = useCallback(
    (id: ShortcutId, combos: string[]) => {
      setPref(PREF_KEY, { ...(prefs[PREF_KEY] as object | undefined), [id]: combos });
    },
    [prefs, setPref]
  );

  const resetAll = useCallback(() => setPref(PREF_KEY, {}), [setPref]);

  const value = useMemo(() => ({ bindings, setBinding, resetAll }), [bindings, setBinding, resetAll]);
  return <ShortcutsContext.Provider value={value}>{children}</ShortcutsContext.Provider>;
}

export function useShortcuts() {
  return useContext(ShortcutsContext);
}

export function useBindings(): Bindings {
  return useContext(ShortcutsContext).bindings;
}
